import React, { useState, useEffect } from 'react';
import {
  IconSun, IconSunCloud, IconCloud, IconFog, IconDrizzle,
  IconRain, IconSnow, IconStorm, IconThermometer,
} from './Icons';

// Default location: London. Open-Meteo's UK forecasts are sourced from
// the UK Met Office (UKMO) model, so this gives Met-Office-grade data
// without needing an API key or managing a secret.
const DEFAULT_LAT = 51.5074;
const DEFAULT_LON = -0.1278;

// WMO weather codes -> icon component + short label.
// Reference: https://open-meteo.com/en/docs (WMO Weather interpretation codes)
const WEATHER_CODES = {
  0:  { Icon: IconSun,      label: 'Clear sky' },
  1:  { Icon: IconSunCloud, label: 'Mainly clear' },
  2:  { Icon: IconSunCloud, label: 'Partly cloudy' },
  3:  { Icon: IconCloud,    label: 'Overcast' },
  45: { Icon: IconFog,      label: 'Fog' },
  48: { Icon: IconFog,      label: 'Freezing fog' },
  51: { Icon: IconDrizzle,  label: 'Light drizzle' },
  53: { Icon: IconDrizzle,  label: 'Drizzle' },
  55: { Icon: IconDrizzle,  label: 'Dense drizzle' },
  61: { Icon: IconRain,     label: 'Light rain' },
  63: { Icon: IconRain,     label: 'Rain' },
  65: { Icon: IconRain,     label: 'Heavy rain' },
  71: { Icon: IconSnow,     label: 'Light snow' },
  73: { Icon: IconSnow,     label: 'Snow' },
  75: { Icon: IconSnow,     label: 'Heavy snow' },
  80: { Icon: IconDrizzle,  label: 'Light showers' },
  81: { Icon: IconRain,     label: 'Rain showers' },
  82: { Icon: IconStorm,    label: 'Violent showers' },
  95: { Icon: IconStorm,    label: 'Thunderstorm' },
  96: { Icon: IconStorm,    label: 'Thunderstorm, hail' },
  99: { Icon: IconStorm,    label: 'Thunderstorm, heavy hail' },
};

function describe(code) {
  return WEATHER_CODES[code] || { Icon: IconThermometer, label: 'Weather' };
}

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function fetchWeather(lat, lon) {
  const url = `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}` +
    `&current=temperature_2m,precipitation,weather_code&daily=temperature_2m_max,temperature_2m_min,precipitation_sum,weather_code` +
    `&timezone=Europe%2FLondon&forecast_days=5&past_days=14`;
  return fetch(url).then(resp => {
    if (!resp.ok) throw new Error('Weather service unavailable');
    return resp.json();
  });
}

function Shell({ place, children }) {
  return (
    <section id="forecast" aria-labelledby="forecast-heading">
      <div className="section-head">
        <div>
          <p className="eyebrow">Today</p>
          <h2 className="section-title" id="forecast-heading">Forecast</h2>
        </div>
        {place && <p className="section-note">{place}</p>}
      </div>
      <div className="weather card">{children}</div>
    </section>
  );
}

export default function WeatherWidget() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [usingDeviceLocation, setUsingDeviceLocation] = useState(false);

  useEffect(() => {
    // Ask the browser for the visitor's location so the forecast matches
    // their actual garden. This triggers the browser's own "Allow this
    // site to know your location?" permission prompt - nothing is sent
    // anywhere unless the visitor explicitly clicks Allow. If they deny,
    // dismiss, or geolocation isn't available, we fall back to London.
    if (!navigator.geolocation) {
      fetchWeather(DEFAULT_LAT, DEFAULT_LON).then(setData).catch(err => setError(err.message));
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setUsingDeviceLocation(true);
        fetchWeather(position.coords.latitude, position.coords.longitude)
          .then(setData)
          .catch(err => setError(err.message));
      },
      () => {
        // Permission denied or unavailable - fall back to London.
        fetchWeather(DEFAULT_LAT, DEFAULT_LON).then(setData).catch(err => setError(err.message));
      },
      { timeout: 8000 }
    );
  }, []);

  if (error) {
    return (
      <Shell>
        <p className="weather__source" style={{ border: 0, marginTop: 0 }}>
          Forecast temporarily unavailable.
        </p>
      </Shell>
    );
  }

  if (!data) {
    return (
      <Shell>
        <p className="weather__source" style={{ border: 0, marginTop: 0 }}>
          Loading forecast...
        </p>
      </Shell>
    );
  }

  const { current, daily } = data;
  const now = describe(current.weather_code);
  const NowIcon = now.Icon;

  // Drought signal: total rainfall over the last 14 days vs a rough
  // "healthy" threshold for the growing season (~15mm/week is a common
  // UK rule-of-thumb minimum for established beds).
  const last14DaysRain = (daily.precipitation_sum || []).slice(0, 14).reduce((sum, v) => sum + (v || 0), 0);
  const isDry = last14DaysRain < 15;

  // Forecast days start after the past_days window - only show today onward.
  const todayIndex = daily.time.findIndex(d => new Date(d) >= new Date(new Date().toDateString()));
  const forecastDays = daily.time.slice(todayIndex, todayIndex + 5);

  return (
    <Shell place={usingDeviceLocation ? 'Your location' : 'London'}>
      <div className="weather__now">
        <span className="weather__icon"><NowIcon size={40} /></span>
        <span>
          <span className="weather__temp">{Math.round(current.temperature_2m)}&deg;C</span>
          <span className="weather__desc">{now.label}</span>
        </span>
      </div>

      <div className="weather__days">
        {forecastDays.map((day, i) => {
          const idx = todayIndex + i;
          const d = describe(daily.weather_code[idx]);
          const DayIcon = d.Icon;
          return (
            <div className="weather__day" key={day}>
              <span className="weather__day-name">
                {i === 0 ? 'Today' : DAY_NAMES[new Date(day).getDay()]}
              </span>
              <span className="weather__day-icon" title={d.label}><DayIcon size={16} /></span>
              <span className="weather__day-desc">{d.label}</span>
              <span className="weather__day-temp">
                {Math.round(daily.temperature_2m_max[idx])}&deg; / {Math.round(daily.temperature_2m_min[idx])}&deg;
              </span>
            </div>
          );
        })}
      </div>

      {isDry && (
        <p className="notice notice--warn">
          <strong>Low rainfall:</strong> {last14DaysRain.toFixed(1)}mm in 14 days.
          Water deeply two or three times a week rather than little and often,
          ideally morning or evening.
        </p>
      )}

      <p className="weather__source">
        Data: <a href="https://open-meteo.com/" target="_blank" rel="noopener noreferrer">Open-Meteo</a>, UK Met Office model
      </p>
    </Shell>
  );
}
